// AI-CONTEXT: See docs/features/update-aanvragen.md

import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import Modal from '../common/Modal'
import {
    UPDATE_REQUEST_STATUSES,
    UPDATE_REQUEST_STATUS_LABELS,
    requiresStatusNote,
    statusNoteIsQuestion,
    missingStatusNoteReason,
} from '../../helpers/updateRequests/updateRequestStatus'
import findUpdateById, { updatesForRequest } from '../../helpers/updates/findUpdateById'
import formatUpdateDate from '../../helpers/updates/formatUpdateDate'
import updates from '../../data/updates.json'

const MAX_NOTE_LENGTH = 2000

/**
 * @typedef {object} UpdateRequest
 * @property {string} ID - Uniek id van het verzoek
 * @property {string} Title - Korte omschrijving
 * @property {string} Status - Huidige stand
 * @property {string} [StatusNote] - Huidig antwoord aan de organisatie, of de openstaande vraag
 * @property {string} [Module] - Categorie van het verzoek; beperkt de keuze van updates
 * @property {string} [UpdateID] - De update uit updates.json die dit verzoek oplevert
 */

/**
 * Venster waarin Deccos de stand van een updateverzoek bijwerkt.
 *
 * Schrijft via de Cloud Function `updateUpdateRequestStatus`, niet rechtstreeks:
 * de regel op UpdateRequests staat op `update: if false`, en de functie bewaakt
 * dat alleen de beheertenant mag wijzigen én dat een afwijzing een toelichting
 * heeft. Die tweede regel staat hier óók, zodat de gebruiker het ziet vóór hij
 * op opslaan drukt — maar de functie is de poort, niet dit venster.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Of het venster open staat
 * @param {(open: boolean) => void} props.setModalOpen
 * @param {UpdateRequest|null} props.request - Het verzoek dat bewerkt wordt
 * @param {() => void} [props.onSaved] - Aangeroepen na een geslaagde wijziging
 */
const UpdateRequestStatusModal = ({ modalOpen, setModalOpen, request, onSaved }) => {

  // State
  const [status, setStatus] = useState('')
  const [note, setNote] = useState('')
  const [updateId, setUpdateId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Het concept volgt het verzoek dat geopend wordt. Bewust op request?.ID en
  // niet op het hele object: een nieuwe Firestore-snapshot levert een nieuwe
  // objectidentiteit op en zou het concept midden in het typen resetten.
  // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
  useEffect(() => {
    setStatus(request?.Status || '')
    setNote(request?.StatusNote || '')
    // Een koppeling naar een update die niet (meer) in de lijst staat, zou de
    // keuzelijst als 'Geen' tonen terwijl het concept het oude id vasthoudt.
    // Dan liever écht 'Geen', zodat scherm en opslag hetzelfde zeggen.
    setUpdateId(findUpdateById(updates, request?.UpdateID) ? request.UpdateID : '')
    setError('')
  }, [request?.ID])

  if (!request) return null

  const noteRequired = requiresStatusNote(status)
  const incomplete = status === '' || (noteRequired && note.trim() === '')
  // Bij 'meer uitleg nodig' schrijf je een vraag, geen antwoord. Hetzelfde
  // tekstvak, andere opdracht — dus ook een ander label.
  const noteIsQuestion = statusNoteIsQuestion(status)
  const noteLabel = noteIsQuestion ? 'Vraag aan de organisatie' : 'Antwoord aan de organisatie'

  // Opslaan
  const saveHandler = async () => {
    if (incomplete || saving) return

    setError('')
    setSaving(true)

    try {
      const updateStatus = firebase.app().functions('europe-west1')
        .httpsCallable('updateUpdateRequestStatus')

      await updateStatus({ RequestID: request.ID, Status: status, StatusNote: note, UpdateID: updateId })

      if (onSaved) onSaved()
      setModalOpen(false)
    } catch (err) {
      setError(err.message || 'Het opslaan is niet gelukt. Probeer het opnieuw.')
    } finally {
      setSaving(false)
    }
  }

  // JSX
  const content = (
    <div className="update-request-status-form">
      <p className="update-request-status-body">{request.Body}</p>

      <label htmlFor="update-request-status">Stand van zaken</label>
      <select
        id="update-request-status"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        {UPDATE_REQUEST_STATUSES.map(slug => (
          <option key={slug} value={slug}>{UPDATE_REQUEST_STATUS_LABELS[slug]}</option>
        ))}
      </select>

      <label htmlFor="update-request-note">{noteLabel}</label>
      <textarea
        id="update-request-note"
        rows={5}
        maxLength={MAX_NOTE_LENGTH}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {/* Dezelfde tekst als de foutmelding van de callable, uit één gedeelde
          bron — anders leest de beheerder hier iets anders dan waarop de
          functie weigert.
          AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment */}
      {noteRequired && note.trim() === '' &&
        <p className="update-request-status-hint">{missingStatusNoteReason(status)}</p>
      }

      {/* De koppeling aan de update die dit verzoek oplevert. De release-
          detector zet een 'gebouwd' verzoek op 'afgerond' zodra die update live
          staat, en mailt dan de aanvrager. Zonder koppeling gebeurt dat niet
          vanzelf. AI-CONTEXT: See docs/features/update-aanvragen-afgerond-mail.md */}
      <label htmlFor="update-request-update">Gekoppelde update</label>
      <select
        id="update-request-update"
        value={updateId}
        onChange={(e) => setUpdateId(e.target.value)}
      >
        <option value="">Geen</option>
        {updatesForRequest(updates, request.Module, request.UpdateID).map(update => (
          <option key={update.id} value={update.id}>
            {formatUpdateDate(update.date)} — {update.title}
          </option>
        ))}
      </select>

      {error &&
        <p className="update-request-error" role="alert">{error}</p>
      }
    </div>
  )

  return (
    <Modal
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      pageHeader={request.Title}
      confirm={saveHandler}
      confirmDisabled={incomplete || saving}
      confirmLabel={saving ? 'Bezig met opslaan…' : 'Opslaan'}
      content={content}
    />
  )
}

export default UpdateRequestStatusModal
